import { ItemGroupI, ItemI, PartInterface, BracketElement } from "../shared/item";
import { ShelvingStoreInterface } from "../store/useShelvingStore";
import { isBracket, isAmplifier, isRod, isEdge, isNotServicePart } from "../utils/typeChecker";

/**
 * Создает карту для быстрого поиска актуальных данных запчастей по ID
 */
function createPartsDataMap(itemsGroup: ItemGroupI[]): Map<string, ItemI> {
    const partsDataMap = new Map<string, ItemI>();

    // Находим группу "Запчасти"
    const partsGroup = itemsGroup.find((group) => group?.NAME?.toLowerCase().includes("запчасти"));

    if (!partsGroup?.ELEMENTS) {
        console.warn("Parts group not found in itemsGroup");
        return partsDataMap;
    }

    // Создаем карту ID -> полные данные запчасти
    partsGroup.ELEMENTS.forEach((part: ItemI) => {
        if (part.ID) {
            partsDataMap.set(part.ID, part);
        }
    });

    return partsDataMap;
}

/**
 * Создает карту для поиска оригинальных элементов по bitrixId
 */
function createOriginalItemsMap(itemsGroup: ItemGroupI[]): Map<string, ItemI> {
    const itemsMap = new Map<string, ItemI>();

    itemsGroup.forEach((group: ItemGroupI) => {
        if (!group.ELEMENTS) return;

        group.ELEMENTS.forEach((item: ItemI) => {
            if (item.BITRIX_OFFER_ID) {
                itemsMap.set(item.BITRIX_OFFER_ID, item);
            }
            if (item.BITRIX_OFFER_ID_ALT) {
                itemsMap.set(item.BITRIX_OFFER_ID_ALT, item);
            }
            if (item.BITRIX_OFFERS_IDS) {
                item.BITRIX_OFFERS_IDS.forEach((id) => {
                    if (id) {
                        itemsMap.set(id, item);
                    }
                });
            }
        });
    });

    return itemsMap;
}

/**
 * Создает части по типу на основе PARTS массива оригинального элемента
 */
function createPartsFromOriginal(
    originalItem: ItemI,
    partsDataMap: Map<string, ItemI>,
    position: number[]
): {
    brackets: any;
    amps: any;
    rods: (PartInterface & { bitrixIds?: any[] })[];
    edges: (PartInterface & { bitrixIds?: any[] })[];
    parts: (PartInterface & { bitrixIds?: any[] })[];
} {
    if (!originalItem.PARTS) {
        return {
            brackets: {
                left: { elements: [], isVisible: false, position },
                right: { elements: [], isVisible: false, position },
                isExist: false,
            },
            amps: {
                left: { elements: [], isVisible: false, position },
                right: { elements: [], isVisible: false, position },
                isExist: false,
            },
            rods: [],
            edges: [],
            parts: [],
        };
    }

    // Фильтруем части по типам
    const currentBrackets = originalItem.PARTS.map((partId) => partsDataMap.get(partId)).filter(
        (part): part is ItemI => part != null && part.TYPE && isBracket(part.TYPE)
    );

    const currentAmps = originalItem.PARTS.map((partId) => partsDataMap.get(partId)).filter(
        (part): part is ItemI => part != null && part.TYPE && isAmplifier(part.TYPE)
    );

    const currentRods = originalItem.PARTS.map((partId) => partsDataMap.get(partId)).filter(
        (part): part is ItemI => part != null && part.TYPE && isRod(part.TYPE)
    );

    const currentEdges = originalItem.PARTS.map((partId) => partsDataMap.get(partId)).filter(
        (part): part is ItemI => part != null && part.TYPE && isEdge(part.TYPE)
    );

    const currentParts = originalItem.PARTS.map((partId) => partsDataMap.get(partId)).filter(
        (part): part is ItemI => part != null && part.TYPE && isNotServicePart(part.TYPE)
    );

    // Создаем brackets
    const bracketElements = currentBrackets.map((bracket) => {
        const holes = bracket?.PROPERTIES?.RAIL_HOLES;
        return {
            bitrixId: bracket?.BITRIX_OFFER_ID || "",
            bitrixIds: bracket?.BITRIX_OFFERS_IDS || [],
            model: bracket?.PROPERTIES.FILE_3D || "",
            modelId: bracket?.ID || "",
            name: bracket?.NAME,
            type: bracket?.TYPE,
            price: Number(bracket?.PRICE) || 0,
            railHoles:
                holes &&
                holes.map((hole: any) => ({
                    leftRightRail: hole.leftRightRail === "Y",
                    leftLeftRail: hole.leftLeftRail === "Y",
                    rightLeftRail: hole.rightLeftRail === "Y",
                    rightRightRail: hole.rightRightRail === "Y",
                    depthFrom: +hole.depthFrom,
                    depthTo: +hole.depthTo,
                    sort: +hole.sort,
                })),
        };
    });

    const brackets = {
        left: { elements: bracketElements, isVisible: true, position },
        right: { elements: bracketElements, isVisible: true, position },
        isExist: !!currentBrackets[0]?.PROPERTIES.FILE_3D,
    };

    // Создаем amps
    const ampElements = currentAmps.map((amp) => {
        const holes = amp?.PROPERTIES?.RAIL_HOLES;
        return {
            bitrixId: amp?.BITRIX_OFFER_ID || "",
            bitrixIds: amp?.BITRIX_OFFERS_IDS || [],
            model: amp?.PROPERTIES.FILE_3D || "",
            modelId: amp?.ID || "",
            name: amp?.NAME,
            type: amp?.TYPE,
            price: Number(amp?.PRICE) || 0,
            picture: amp?.PICTURE,
            railHoles:
                holes &&
                holes.map((hole: any) => ({
                    leftRightRail: hole.leftRightRail === "Y",
                    leftLeftRail: hole.leftLeftRail === "Y",
                    rightLeftRail: hole.rightLeftRail === "Y",
                    rightRightRail: hole.rightRightRail === "Y",
                    depthFrom: +hole.depthFrom,
                    depthTo: +hole.depthTo,
                    sort: +hole.sort,
                })),
        };
    });

    const amps = {
        left: { elements: ampElements, isVisible: true, position },
        right: { elements: ampElements, isVisible: true, position },
        isExist: !!currentAmps[0]?.PROPERTIES.FILE_3D,
    };

    // Создаем rods
    const rods = currentRods.map((rod) => {
        const holes = rod?.PROPERTIES?.RAIL_HOLES;
        let rodZ = +originalItem.PROPERTIES.DEPTH / 2000 || 0.5;
        if (originalItem.PROPERTIES.DEPTH === "520") {
            rodZ = 520 / 2000;
        }

        return {
            isVisible: !!rod?.PROPERTIES.FILE_3D,
            bitrixId: rod?.BITRIX_OFFER_ID || "",
            bitrixIds: rod?.BITRIX_OFFERS_IDS || [],
            bitrixIdOriginal: rod?.BITRIX_OFFER_ID || "",
            bitrixIdAlt: rod?.BITRIX_OFFER_ID_ALT,
            model: rod?.PROPERTIES.FILE_3D || "",
            modelId: rod?.ID || "",
            name: rod?.NAME,
            type: rod?.TYPE,
            price: Number(rod?.PRICE) || 0,
            picture: rod?.PICTURE,
            railHoles:
                holes &&
                holes.map((hole: any) => ({
                    leftRightRail: hole.leftRightRail === "Y",
                    leftLeftRail: hole.leftLeftRail === "Y",
                    rightLeftRail: hole.rightLeftRail === "Y",
                    rightRightRail: hole.rightRightRail === "Y",
                    depthFrom: +hole.depthFrom,
                    depthTo: +hole.depthTo,
                    sort: +hole.sort,
                })),
            position: [position[0], position[1], rodZ],
        };
    });

    // Создаем edges
    const edges = currentEdges.map((edge) => {
        const holes = edge?.PROPERTIES?.RAIL_HOLES;
        return {
            isVisible: !!edge?.PROPERTIES.FILE_3D,
            bitrixId: edge?.BITRIX_OFFER_ID || "",
            bitrixIds: edge?.BITRIX_OFFERS_IDS || [],
            model: edge?.PROPERTIES.FILE_3D || "",
            modelId: edge?.ID || "",
            name: edge?.NAME,
            type: edge?.TYPE,
            price: Number(edge?.PRICE) || 0,
            picture: edge?.PICTURE,
            railHoles:
                holes &&
                holes.map((hole: any) => ({
                    leftRightRail: hole.leftRightRail === "Y",
                    leftLeftRail: hole.leftLeftRail === "Y",
                    rightLeftRail: hole.rightLeftRail === "Y",
                    rightRightRail: hole.rightRightRail === "Y",
                    depthFrom: +hole.depthFrom,
                    depthTo: +hole.depthTo,
                    sort: +hole.sort,
                })),
            position: [position[0], position[1], +originalItem.PROPERTIES.DEPTH / 2000 || 0.25],
        };
    });

    // Создаем parts
    const parts = currentParts.map((part) => {
        const holes = part?.PROPERTIES?.RAIL_HOLES;
        return {
            isVisible: !!part?.PROPERTIES.FILE_3D,
            bitrixId: part?.BITRIX_OFFER_ID || "",
            bitrixIds: part?.BITRIX_OFFERS_IDS || [],
            bitrixIdOriginal: part?.BITRIX_OFFER_ID || "",
            bitrixIdAlt: part?.BITRIX_OFFER_ID_ALT,
            model: part?.PROPERTIES.FILE_3D || "",
            modelId: part?.ID || "",
            name: part?.NAME,
            type: part?.TYPE,
            price: Number(part?.PRICE) || 0,
            railHoles:
                holes &&
                holes.map((hole: any) => ({
                    leftRightRail: hole.leftRightRail === "Y",
                    leftLeftRail: hole.leftLeftRail === "Y",
                    rightLeftRail: hole.rightLeftRail === "Y",
                    rightRightRail: hole.rightRightRail === "Y",
                    depthFrom: +hole.depthFrom,
                    depthTo: +hole.depthTo,
                    sort: +hole.sort,
                })),
            position: [position[0], position[1], +originalItem.PROPERTIES.DEPTH / 2000 || 0.25],
        };
    });

    return { brackets, amps, rods, edges, parts };
}

/**
 * Рекурсивно обновляет данные всех запчастей в элементе
 */
function updateItemParts(item: any, partsDataMap: Map<string, ItemI>, originalItemsMap: Map<string, ItemI>): any {
    if (!item || typeof item !== "object") return item;

    // Находим оригинальный элемент по bitrixId
    const originalItem = originalItemsMap.get(item.bitrixId);
    if (!originalItem) {
        console.warn(`Original item not found for bitrixId: ${item.bitrixId}`);
        return item?.model ? item : null;
    }

    const updatedParts = createPartsFromOriginal(originalItem, partsDataMap, item.position);

    const brackets = item.brackets
        ? {
              ...item.brackets,
              left: {
                  ...item.brackets.left,
                  elements: (item.brackets?.left?.elements || []).map((el: any) => {
                      const matched =
                          (updatedParts?.brackets?.left?.elements || []).find(
                              (e: any) => e?.bitrixId === el?.bitrixId || e?.bitrixIds?.includes(el.bitrixId)
                          ) || null;

                      if (!matched) return el;

                      const { bitrixIds, ...rest } = matched;
                      return { ...el, ...rest, bitrixId: el.bitrixId };
                  }),
              },
              right: {
                  ...item.brackets.right,
                  elements: (item.brackets?.right?.elements || []).map((el: any) => {
                      const matched =
                          (updatedParts?.brackets?.right?.elements || []).find(
                              (e: any) => e?.bitrixId === el?.bitrixId || e?.bitrixIds?.includes(el.bitrixId)
                          ) || null;

                      if (!matched) return el;

                      const { bitrixIds, ...rest } = matched;
                      return { ...el, ...rest, bitrixId: el.bitrixId };
                  }),
              },
          }
        : updatedParts.brackets;

    const amps = item.amps
        ? {
              ...item.amps,
              left: {
                  ...item.amps.left,
                  elements: ((item.amps?.left?.elements && item.amps?.left?.elements?.length !== 0)
                    ? item.amps?.left?.elements : updatedParts?.amps?.left?.elements).map((el: any) => {
                      const matched =
                          (updatedParts?.amps?.left?.elements || []).find(
                              (e: any) => e?.bitrixId === el?.bitrixId || e?.bitrixIds?.includes(el.bitrixId)
                          ) || null;

                      if (!matched) return el;

                      const { bitrixIds, ...rest } = matched;
                      return { ...el, ...rest, bitrixId: el.bitrixId };
                  }),
              },
              right: {
                  ...item.amps.right,
                  elements: ((item.amps?.right?.elements && item.amps?.right?.elements?.length !== 0)
                    ? item.amps?.right?.elements : updatedParts?.amps?.right?.elements).map((el: any) => {
                      const matched =
                          (updatedParts?.amps?.right?.elements || []).find(
                              (e: any) => e?.bitrixId === el?.bitrixId || e?.bitrixIds?.includes(el.bitrixId)
                          ) || null;

                      if (!matched) return el;

                      const { bitrixIds, ...rest } = matched;
                      return { ...el, ...rest, bitrixId: el.bitrixId };
                  }),
              },
          }
        : updatedParts.amps;

    const parts = (item.parts || []).map((part: any) => {
        const matched =
            [...updatedParts.parts, ...updatedParts.edges, ...updatedParts.rods].find(
                (p: any) => p.bitrixId === part.bitrixId || p?.bitrixIds?.includes(part.bitrixId)
            ) || {};

        // @ts-ignore
        const { bitrixIds, ...matchedRest } = matched;

        return {
            ...part,
            ...matchedRest,
            bitrixId: part.bitrixId,
            position: part.position,
            name: part.name,
        };
    });

    const edges = ((item.edges && item.edges?.length !== 0) ? item.edges : updatedParts.edges).map((part: any) => {
        const matched =
            updatedParts.edges.find(
                (p: any) => p.bitrixId === part.bitrixId || p?.bitrixIds?.includes(part.bitrixId)
            ) || {};

        // @ts-ignore
        const { bitrixIds, ...matchedRest } = matched;

        return {
            ...part,
            ...matchedRest,
            bitrixId: part.bitrixId,
            position: part.position,
            isVisible: part.isVisible,
        };
    });

    const rods = ((item.rods && item.rods?.length !== 0) ? item.rods : updatedParts.rods).map((part: any) => {
        const matched =
            updatedParts.rods.find((p: any) => p.bitrixId === part.bitrixId || p?.bitrixIds?.includes(part.bitrixId)) ||
            {};

        // @ts-ignore
        const { bitrixIds, ...matchedRest } = matched;

        return {
            ...part,
            ...matchedRest,
            bitrixId: part.bitrixId,
            position: part.position,
            isVisible: part.isVisible,
        };
    });

    return {
        ...item,
        brackets,
        amps,
        rods,
        edges,
        parts,
        type: originalItem?.TYPE || item.type,
    };
}

/**
 * Обновляет данные всех запчастей в проекте из свежих данных
 */
export function updateAllPartsData(
    project: {
        shelvingState?: DeepPartial<ShelvingStoreInterface> | undefined;
    },
    itemsGroup: ItemGroupI[]
): typeof project {
    if (!itemsGroup || itemsGroup.length === 0) {
        console.warn("No itemsGroup provided for parts update");
        return project;
    }

    if (!project.shelvingState?.shelving) {
        return project;
    }

    const partsDataMap = createPartsDataMap(itemsGroup);
    const originalItemsMap = createOriginalItemsMap(itemsGroup);

    if (partsDataMap.size === 0) {
        console.warn("No parts data found in itemsGroup");
        return project;
    }

    if (originalItemsMap.size === 0) {
        console.warn("No original items data found in itemsGroup");
        return project;
    }

    const updatedProject = { ...project };
    const sides = ["front", "left", "right", "back"] as const;

    // Обновляем данные запчастей для каждой стороны
    sides.forEach((side) => {
        const wallShelving = updatedProject.shelvingState?.shelving?.[side];
        if (!wallShelving?.sections) return;

        wallShelving.sections = wallShelving.sections.map((section) => ({
            ...section,
            items:
                section?.items?.map((item) => updateItemParts(item, partsDataMap, originalItemsMap)).filter((i) => i) ||
                [],
        }));
    });

    console.log("Parts data updated successfully");
    return updatedProject;
}
